import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/api.auth";
import { resolveImageSrc } from "../utils/imageUrl";

// IMAGE SIZE 1200*630

function buildWebListFormData(form, imageFile) {
  const formData = new FormData();
  formData.append("name", form.name);
  formData.append("link1name", form.link1name);
  formData.append("link1url", form.link1url);
  formData.append("link2name", form.link2name);
  formData.append("link2url", form.link2url);
  formData.append("introtext", form.introtext);
  if (imageFile) {
    formData.append("image", imageFile);
  }
  return formData;
}

function Edit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    link1name: "",
    link1url: "",
    link2name: "",
    link2url: "",
    introtext: "",
  });
  /** Fields needed to resolve stored photo URL against API origin (not React dev server). */
  const [existingPhotoMeta, setExistingPhotoMeta] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [newPreviewUrl, setNewPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchWebList = async () => {
      setError("");
      setLoading(true);
      try {
        const res = await API.get(`/webList/${id}`);
        const webL = res.data;
        setForm({
          name: webL?.name ?? "",
          link1name: webL?.link1name ?? "",
          link1url: webL?.link1url ?? "",
          link2name: webL?.link2name ?? "",
          link2url: webL?.link2url ?? "",
          introtext: webL?.introtext ?? "",
        });
        setExistingPhotoMeta({
          image_url: webL?.image_url ?? null,
          image: webL?.image ?? null,
          updated_at: webL?.updated_at ?? null,
        });
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : "") ||
          err?.message ||
          "Failed to load weblists";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchWebList();
  }, [id]);

  useEffect(() => {
    return () => {
      if (newPreviewUrl) {
        URL.revokeObjectURL(newPreviewUrl);
      }
    };
  }, [newPreviewUrl]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setImageFile(file);
    if (newPreviewUrl) {
      URL.revokeObjectURL(newPreviewUrl);
    }
    setNewPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      const formData = buildWebListFormData(form, imageFile);
      await API.post(`/webList/${id}`, formData);
      navigate("/AdminHomeScreen");
    } catch (err) {
      const data = err?.response?.data;
      let message =
        data?.message ||
        (typeof data === "string" ? data : "") ||
        err?.message ||
        "Failed to update weblist";
      if (data?.errors && typeof data.errors === "object") {
        const first = Object.values(data.errors).flat()[0];
        if (first) message = first;
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading weblist...</div>;
  }

  const existingResolved = resolveImageSrc(existingPhotoMeta, {
    cacheBust: true,
  });
  const displayPhoto = newPreviewUrl || existingResolved;

  return (
    <div className="form-card">
      <h2>Edit Weblist</h2>

      {error ? <div className="error-banner">{error}</div> : null}

      <form onSubmit={handleSubmit} className="form-grid">
        <div className="field field-full">
          <label htmlFor="edit-image">Image</label>
          <input
            id="edit-image"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileChange}
          />
          <p className="field-hint">
            Leave empty to keep the current photo. JPEG, PNG, WebP, or GIF — max
            2 MB
          </p>
          {displayPhoto ? (
            <div className="iamge-preview">
              <img src={displayPhoto} alt="" decoding="async" />
            </div>
          ) : null}
        </div>

        <div className="field">
          <label>Name</label>
          <input
            placeholder="Name"
            value={form.name}
            onChange={handleChange("name")}
            required
          />
        </div>

        <div className="field">
          <label>Link 1 Name</label>
          <input
            placeholder="link1name"
            value={form.link1name}
            onChange={handleChange("link1name")}
          />
        </div>

        <div className="field">
          <label>Link 1 Url</label>
          <input
            placeholder="link1url"
            value={form.link1url}
            onChange={handleChange("link1url")}
          />
        </div>

        <div className="field">
          <label>Link 2 Name</label>
          <input
            placeholder="link2name"
            value={form.link2name}
            onChange={handleChange("link2name")}
          />
        </div>

        <div className="field">
          <label>Link 2 Url</label>
          <input
            placeholder="link2url"
            value={form.link2url}
            onChange={handleChange("link2url")}
          />
        </div>

        <div className="field">
          <label>Intro Text</label>
          <input
            placeholder="introtext"
            value={form.introtext}
            onChange={handleChange("introtext")}
          />
        </div>

        <button type="submit" disabled={saving}>
          {saving ? "Saving..." : "Save changes"}
        </button>
      </form>
    </div>
  );
}

export default Edit;
